/**
 * Contexto de la visita: qué restaurante y desde qué mesa.
 *
 * El slug viene del primer segmento de la ruta (`/cartazo`) porque cada local
 * necesita una URL propia y compartible. La mesa llega por `?mesa=12`, que es lo
 * que inyecta el resolutor de QR del servidor.
 */

export type VisitContext = {
  slug: string | null
  tableLabel: string | null
  /**
   * Plato al que abrir directamente, por su slug.
   *
   * Lo usa el enlace "Ver en la carta" del editor. Sin esto, revisar un
   * cambio obligaba a abrir la portada y navegar a mano hasta el plato — en
   * una carta de ochenta, eso es no comprobarlo.
   *
   * Va por slug y no por id: es lo que ya identifica al plato de forma única
   * dentro del local, y una URL con un UUID no la reconoce nadie.
   */
  productSlug: string | null
}

export function readContext(): VisitContext {
  if (typeof window === 'undefined') {
    return { slug: null, tableLabel: null, productSlug: null }
  }

  const segments = window.location.pathname.split('/').filter(Boolean)
  const candidate = segments[0] ?? null

  // Solo se acepta un slug con la forma que valida el contrato. Sin esto,
  // cualquier ruta accidental provocaría una petición inútil a la API.
  const slug = candidate && /^[a-z0-9][a-z0-9-]{1,48}[a-z0-9]$/.test(candidate) ? candidate : null

  const params = new URLSearchParams(window.location.search)
  const raw = params.get('mesa')?.trim() ?? null

  // Se acota la longitud: la etiqueta acaba dentro de un mensaje de WhatsApp y
  // no queremos que un parámetro manipulado inyecte texto arbitrario en él.
  const tableLabel = raw && raw.length > 0 && raw.length <= 40 ? raw : null

  // La misma forma que valida el contrato para un slug, por el mismo motivo:
  // un parámetro cualquiera no debe llegar a buscarse en el índice.
  const plato = params.get('plato')?.trim() ?? null
  const productSlug = plato && /^[a-z0-9][a-z0-9-]{0,58}[a-z0-9]$/.test(plato) ? plato : null

  return { slug, tableLabel, productSlug }
}
